import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { V2_COLORS } from './tokens';
import { V2Nav, V2Footer, V2Kicker, V2_REVEAL as REVEAL } from './V2Shared';
import { CASE_STUDIES, CASE_ORDER } from './caseStudies';
import { getGa4Ids } from '../../utils/ga4';
import { useDocumentMeta } from './useDocumentMeta';
import './v2.css';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fraunces = () => ({ fontFamily: 'Fraunces', fontStyle: 'italic', fontWeight: 300, fontOpticalSizing: 'auto' });

/* ---------------- WORK INDEX ---------------- */
export function WorkIndex() {
  useDocumentMeta(
    'Work · BrightLabs — Selected case studies',
    'A selection of live projects — SugaSpice, Brightridge, Cobra Thai and VeroBill. Brands built, sites shipped, software written, growth engines wired up.',
    '/work'
  );
  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid="v2-work-index">
      <V2Nav scheme="dark" active="work" />
      <section className="pt-40 pb-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="—">Selected Work</V2Kicker>
        <motion.h1 {...REVEAL} className="v2-display mt-6 max-w-[14ch] leading-[0.9]">
          Work that <em style={fraunces()}>earns</em> its space.
        </motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.15 }} className="v2-lede mt-8 max-w-[62ch] text-[#B8B4A6]">
          A selection of recent projects. Brands built, sites shipped, software written, growth engines wired up. Every one still live — visit the URLs, judge the work.
        </motion.p>
      </section>

      <section className="border-t border-white/[0.08] py-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-16">
          {CASE_ORDER.map((slug, i) => {
            const c = CASE_STUDIES[slug];
            return (
              <motion.div key={slug} {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.06 }}>
                <WorkCard c={c} i={i} />
              </motion.div>
            );
          })}
        </div>
      </section>
      <V2Footer />
    </div>
  );
}

/* ---------------- WORK CARD (with pointer tilt + filled mockup) ---------------- */
function WorkCard({ c, i }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 18, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 120, damping: 18, mass: 0.4 });
  const rotX = useTransform(sy, [-0.5, 0.5], [4.5, -4.5]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-6, 6]);
  const glareBg = useTransform(
    [sx, sy],
    ([x, y]) => `radial-gradient(30% 30% at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, ${c.accent}55, transparent 70%)`
  );

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleLeave = () => { mx.set(0); my.set(0); };

  const card = c.card || {};
  const chips = card.chips || c.hero?.pills?.slice(0, 4) || [];
  const stats = card.stats || [];

  return (
    <Link to={`/work/${c.slug}`} className="group block" data-testid={`v2-work-card-${c.slug}`}>
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1200, transformStyle: 'preserve-3d' }}
        className="relative aspect-[4/3] rounded-sm overflow-hidden mb-6 will-change-transform"
      >
        {/* Outer bloom */}
        <div
          className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-100 opacity-90"
          style={{ background: `radial-gradient(120% 90% at ${i % 2 ? '85% 15%' : '15% 85%'}, ${c.accent}22 0%, transparent 60%), #0F0F16` }}
        />
        {/* Cursor glare */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-screen"
          style={{ background: glareBg }}
        />

        {/* Browser mockup — real client screenshot */}
        <div className="absolute inset-6 md:inset-10 rounded-sm bg-[#07070C] ring-1 ring-white/10 overflow-hidden flex flex-col" style={{ transform: 'translateZ(30px)' }}>
          {/* Top chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 bg-[#07070C] z-10">
            <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
            <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
            <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
            <span className="ml-3 v2-mono text-[9px] text-white/40 tracking-wider">{c.url}</span>
            <span className="ml-auto v2-mono text-[8px] text-white/30 tracking-wider inline-flex items-center gap-1">
              <span className="w-1 h-1 rounded-full" style={{ background: c.accent, boxShadow: `0 0 6px ${c.accent}` }} /> LIVE
            </span>
          </div>

          {/* Screenshot canvas — clean top slice */}
          <div className="relative overflow-hidden shrink-0" style={{ height: '44%' }}>
            {c.shot ? (
              <img
                src={c.shot}
                alt={`${c.name} — live site`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                draggable={false}
              />
            ) : (
              <div className="absolute inset-0" style={{ background: `radial-gradient(80% 60% at 20% 30%, ${c.accent}22, transparent 60%), #0A0A0F` }} />
            )}
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#07070C] to-transparent" />
          </div>

          {/* Meta strip — solid dark bg, our story */}
          <div className="flex-1 min-h-0 px-5 md:px-6 pt-3.5 pb-3 flex flex-col gap-2 bg-[#07070C]">
            <div className="v2-mono text-[11px] md:text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: c.accent }}>
              {c.discipline.slice(0, 3).join(' · ')}
            </div>
            <div className="text-[24px] md:text-[30px] leading-[0.95]" style={{ ...fraunces(), color: '#F5F1E8' }}>{c.name}</div>
            <div className="text-[12px] md:text-[13px] text-[#B8B4A6] leading-snug max-w-[42ch]">{c.tagline}</div>

            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {chips.slice(0, 4).map((p) => (
                  <span
                    key={p}
                    className="v2-mono text-[9.5px] uppercase tracking-[0.2em] px-2 py-[3px] rounded-full ring-1"
                    style={{ color: c.accent, borderColor: `${c.accent}55`, background: `${c.accent}12` }}
                  >
                    {p}
                  </span>
                ))}
              </div>
            )}

            {stats.length > 0 && (
              <div className="mt-auto grid grid-cols-3 gap-3 pt-2.5 border-t border-white/[0.08]">
                {stats.map((s) => (
                  <div key={s.k}>
                    <div className="v2-mono text-[9.5px] uppercase tracking-[0.22em] text-[#9C978A]">{s.k}</div>
                    <div className="mt-0.5 text-[13px] md:text-[15px] leading-none" style={{ ...fraunces(), color: '#F5F1E8' }}>{s.v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom URL bar */}
          <div className="px-4 py-2 border-t border-white/[0.06] flex items-center justify-between v2-mono text-[9px] tracking-[0.2em] uppercase bg-[#07070C] z-10">
            <span className="text-[#9C978A]">Live at {c.url}</span>
            <span className="inline-flex items-center gap-1 text-white/60 group-hover:text-white transition-colors">
              View case study <ArrowUpRight size={10} />
            </span>
          </div>
        </div>
      </motion.div>

      {/* Outer meta */}
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <h3 className="v2-h3 text-white">{c.name}</h3>
          <p className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] mt-1.5">{c.sector}</p>
          {card.resultLine && (
            <p className="v2-mono text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: c.accent }}>
              {card.resultLine}
            </p>
          )}
        </div>
        <span className="v2-mono text-[10px] tracking-[0.24em] text-[#9C978A] shrink-0">{c.year}</span>
      </div>
    </Link>
  );
}
export function WorkDetail() {
  const { slug } = useParams();
  const c = CASE_STUDIES[slug] || CASE_STUDIES.sugaspice;
  const nextSlug = CASE_ORDER[(CASE_ORDER.indexOf(slug) + 1) % CASE_ORDER.length];
  const next = CASE_STUDIES[nextSlug];
  useDocumentMeta(
    `${c.name} · Case Study · BrightLabs`,
    `${c.tagline} ${c.brief.slice(0, 120)}${c.brief.length > 120 ? '…' : ''}`,
    `/work/${c.slug}`
  );

  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid={`v2-work-${c.slug}`}>
      <V2Nav scheme="dark" active="work" />

      <section className="pt-40 pb-14 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <Link to="/work" className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A] hover:text-white transition-colors mb-14 inline-block">← All work</Link>
        <div className="v2-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: c.accent }}>{c.discipline.join(' · ')}</div>
        <motion.h1 {...REVEAL} className="mt-6 leading-[0.9] max-w-[14ch]" style={{ ...fraunces(), color: c.accent, fontSize: 'clamp(72px, 12vw, 200px)' }}>{c.name}</motion.h1>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.1 }} className="v2-h2 mt-6 max-w-[24ch] text-white">{c.tagline}</motion.p>

        <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.2 }} className="mt-14 grid md:grid-cols-4 gap-6 v2-mono text-[10px] uppercase tracking-[0.28em] text-[#B8B4A6] border-t border-white/[0.08] pt-8">
          <div><div className="text-[#9C978A] mb-1">Discipline</div><div className="text-white">{c.discipline.join(' · ')}</div></div>
          <div><div className="text-[#9C978A] mb-1">Sector</div><div className="text-white">{c.sector}</div></div>
          <div><div className="text-[#9C978A] mb-1">Year</div><div className="text-white">{c.year}</div></div>
          <div><div className="text-[#9C978A] mb-1">Live at</div><a href={`https://www.${c.url}`} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#E85D2F] transition-colors inline-flex items-center gap-1.5">{c.url} <ArrowUpRight size={10} /></a></div>
        </motion.div>
      </section>

      <section className="border-t border-white/[0.08] py-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto grid md:grid-cols-2 gap-14">
        <div>
          <V2Kicker num="01" warm>The brief</V2Kicker>
          <p className="v2-lede text-[#CFCBBD] mt-6 max-w-[50ch] text-[19px] leading-relaxed">{c.brief}</p>
        </div>
        <div>
          <V2Kicker num="02" warm>What we built</V2Kicker>
          <ul className="mt-6 space-y-3">
            {c.built.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px] text-[#CFCBBD] leading-relaxed">
                <span className="v2-mono" style={{ color: c.accent }}>—</span>
                <span dangerouslySetInnerHTML={{ __html: b }} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Live site mockup — real screenshot */}
      <section className="border-t border-white/[0.08] py-24 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div className="relative aspect-[16/10] rounded-sm overflow-hidden ring-1 ring-white/10" style={{ background: `radial-gradient(80% 60% at 20% 30%, ${c.accent}22 0%, transparent 60%), #0A0A0F` }}>
          <div className="absolute inset-6 md:inset-14 rounded-sm bg-[#07070C] ring-1 ring-white/10 overflow-hidden flex flex-col">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-white/5 bg-[#07070C]">
              <span className="w-2 h-2 rounded-full bg-white/20" /><span className="w-2 h-2 rounded-full bg-white/20" /><span className="w-2 h-2 rounded-full bg-white/20" />
              <span className="ml-3 v2-mono text-[10px] text-white/40 tracking-wider">https://www.{c.url}</span>
              <span className="ml-auto v2-mono text-[9px] tracking-wider inline-flex items-center gap-1.5" style={{ color: c.accent }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.accent, boxShadow: `0 0 8px ${c.accent}` }} /> LIVE
              </span>
            </div>
            <div className="relative flex-1 overflow-hidden">
              {c.shot ? (
                <img src={c.shot} alt={`${c.name} — live site`} className="absolute inset-0 w-full h-full object-cover object-top" draggable={false} />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center v2-mono text-[10px] uppercase tracking-[0.28em] text-white/30">Screenshot pending</div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A] text-center">
          Captured live from <a href={`https://www.${c.url}`} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#E85D2F] transition-colors">{c.url}</a>
        </div>
      </section>

      {/* Three highlights — the story of what changed */}
      <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="03" warm>The moves that mattered</V2Kicker>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {c.highlights.map((h, i) => (
            <motion.div key={i} {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.06 }} className="border-t border-white/[0.08] pt-6">
              <span className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">0{i + 1}</span>
              <h4 className="v2-h3 mt-3 text-white text-[22px] leading-tight">
                <em style={{ ...fraunces(), color: c.accent }}>{h.h}</em>
              </h4>
              <p className="mt-4 text-[15px] text-[#CFCBBD] leading-relaxed" dangerouslySetInnerHTML={{ __html: h.p }} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery strip — what got built */}
      <section className="border-t border-white/[0.08] py-24 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <V2Kicker num="04" warm>What we shipped</V2Kicker>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
          {c.gallery.map((g, i) => (
            <motion.div
              key={g}
              {...REVEAL}
              transition={{ ...REVEAL.transition, delay: 0.03 * i }}
              className="aspect-[4/3] rounded-sm overflow-hidden ring-1 ring-white/10 relative flex flex-col justify-end p-5"
              style={{ background: `radial-gradient(60% 60% at ${(i * 30) % 100}% ${(i * 40) % 100}%, ${c.accent}25 0%, transparent 65%), #0A0A0F` }}
            >
              <div className="v2-mono text-[9px] uppercase tracking-[0.24em] text-[#9C978A]">Discipline · {c.discipline[i % c.discipline.length]}</div>
              <div className="mt-2 v2-h3 text-white text-[18px]" style={{ ...fraunces() }}>{g}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.08] py-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto grid md:grid-cols-2 gap-14">
        <div>
          <V2Kicker num="03">Outcome</V2Kicker>
          <p className="v2-lede text-white mt-6 max-w-[46ch] text-[22px] leading-snug">
            <em style={fraunces()}>{c.what}</em>
          </p>
        </div>
        <div>
          <V2Kicker num="04">Live &amp; verifiable</V2Kicker>
          <p className="mt-6 text-[#CFCBBD] text-[15px] leading-relaxed max-w-[46ch]">{c.proof}</p>
          <a href={`https://www.${c.url}`} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 v2-mono text-[11px] uppercase tracking-[0.28em] text-white hover:text-[#E85D2F] transition-colors" data-testid={`v2-work-url-${c.slug}`}>Visit {c.url} <ArrowUpRight size={13} /></a>
        </div>
      </section>

      {/* Next project */}
      <section className="border-t border-white/[0.08] py-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <Link to={`/work/${next.slug}`} className="group block" data-testid="v2-work-next">
          <V2Kicker num="→">Next project</V2Kicker>
          <div className="mt-6 grid md:grid-cols-[1fr_auto] items-end gap-6">
            <h3 className="v2-display leading-[0.95] max-w-[18ch]" style={{ fontSize: 'clamp(48px,7vw,120px)' }}>
              {next.name.split(' ').map((w, i, arr) => (
                <span key={i}>{i === arr.length - 1 ? <em style={{ ...fraunces(), color: next.accent }}>{w}</em> : <>{w} </>}</span>
              ))}
            </h3>
            <ArrowUpRight size={44} className="text-[#9C978A] group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
          </div>
          <p className="mt-4 v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A]">{next.discipline.join(' · ')} · {next.year}</p>
        </Link>
      </section>

      <V2Footer />
    </div>
  );
}

/* ---------------- SMALL BUSINESS ---------------- */
export function SmallBusiness() {
  useDocumentMeta(
    'Small Business · Bright Start & Bright Local · BrightLabs',
    'Productised offer for sole traders, small local businesses and start-ups. Bright Start from £149 setup + £99/mo. Bright Local from £299 setup + £249/mo.',
    '/small-business'
  );
  const pkgs = [
    {
      name: 'Bright Start',    setup: '£149',  monthly: '£99/mo',  daily: '≈ £3/day',
      tag: 'Get your business online — professionally.', for: 'Sole traders, brand-new businesses, side-hustles.',
      inc: [
        'Professionally designed, mobile-friendly website — up to 5 core pages',
        'Contact and enquiry forms with clear calls to action',
        'Hosting, SSL and routine technical maintenance handled',
        'Google Search Console set up + submitted for indexing',
        'Basic on-page SEO — page titles and meta descriptions',
        'Up to 30 minutes of light website amendments per month',
        'Up to £15 towards a standard domain for the first 12 months',
        'Target launch within 14 days once content received',
      ],
    },
    {
      name: 'Bright Local',    setup: '£299',  monthly: '£249/mo', daily: '≈ £8/day',
      tag: 'Show up on Google. Show up in your town.', for: 'Established local businesses ready to be found on Google.', popular: true,
      inc: [
        'Everything in Bright Start, plus',
        'Local SEO setup — Google Business Profile fully optimised',
        'ONE new SEO landing page written every month',
        'Google Business Profile managed — posts, photos, review responses',
        'Monthly rank report vs local competitors',
        '6-month minimum term',
      ],
    },
  ];
  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.paperCream, color: V2_COLORS.ink }} data-testid="v2-small-business">
      <V2Nav scheme="light" active="services" />
      <section className="pt-40 pb-16 px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto">
        <V2Kicker num="—" warm>Small Business</V2Kicker>
        <h1 className="v2-display mt-6 max-w-[14ch]" style={{ color: V2_COLORS.ink }}>
          Simple digital, done <em style={fraunces()}>properly</em>.
        </h1>
        <p className="v2-lede mt-8 max-w-[60ch] text-[#3F3A2E]">
          A dedicated productised offer for sole traders, small local businesses and start-ups. Fixed inclusions, published pricing, simpler onboarding — same standards.
        </p>
      </section>

      <section className="pb-24 px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {pkgs.map((p) => (
            <div key={p.name} className="p-8 md:p-10 rounded-sm ring-1 ring-black/10 bg-white/50 relative" data-testid={`v2-pkg-${p.name.toLowerCase().replace(/\s+/g,'-')}`}>
              {p.popular && <div className="absolute top-4 right-4 v2-mono text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#E85D2F] text-white">Most Popular</div>}
              <div className="v2-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: V2_COLORS.warm }}>{p.name}</div>
              <div className="v2-h1 mt-3 max-w-[16ch]" style={{ fontSize: 'clamp(28px,3vw,42px)', color: V2_COLORS.ink }}>{p.tag}</div>
              <div className="mt-4 text-[13px] text-[#3F3A2E] italic">{p.for}</div>
              <div className="mt-8 flex items-baseline gap-3 pb-6 border-b border-black/10">
                <span className="text-3xl font-black text-[#1A1A22]">{p.setup}</span>
                <span className="v2-mono text-[11px] uppercase tracking-widest text-[#9C978A]">setup +</span>
                <span className="text-3xl font-black text-[#1A1A22]">{p.monthly}</span>
              </div>
              <div className="mt-2 v2-mono text-[10px] uppercase tracking-widest" style={{ color: V2_COLORS.warm }}>{p.daily}</div>
              <ul className="mt-8 space-y-2.5 text-[14px] text-[#3F3A2E]">
                {p.inc.map((i) => <li key={i} className="flex items-start gap-2"><span className="text-[#E85D2F]">—</span><span>{i}</span></li>)}
              </ul>
              <Link to="/start" className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#07070C] text-[#F5F1E8] v2-mono text-[10px] tracking-[0.24em] uppercase font-bold hover:bg-[#1A1A22] transition active:scale-[0.98]">Enquire <ArrowRight size={12} /></Link>
            </div>
          ))}
        </div>

        <p className="mt-16 v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] max-w-[70ch]">
          Need something bigger — a proper agency engagement, custom software or a growth partnership? <Link to="/" className="underline text-[#1A1A22]">See the main BrightLabs offer →</Link>
        </p>
      </section>
      <V2Footer />
    </div>
  );
}

/* ---------------- START A PROJECT ---------------- */
export function StartAProject() {
  useDocumentMeta(
    'Start a Project · BrightLabs',
    'Send us a short brief. We review every enquiry and come back within two working days — either with questions, a suggested call, or an honest "we\'re not the right team for this".',
    '/start'
  );

  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');

    const form = e.currentTarget;
    const fd = new FormData(form);
    const services = fd.getAll('services');
    const business = (fd.get('business') || '').toString().trim();
    const goal = (fd.get('goal') || '').toString().trim();
    const budget = (fd.get('budget') || '').toString().trim();
    const timing = (fd.get('timing') || '').toString().trim();
    const name = (fd.get('name') || '').toString().trim();
    const email = (fd.get('email') || '').toString().trim();
    const phone = (fd.get('phone') || '').toString().trim();

    if (!name || !email || !business || !goal) {
      setStatus('error');
      setErrorMsg('Please fill in your name, email, business and what you\'re trying to achieve.');
      return;
    }

    const messageLines = [
      `Services: ${services.length ? services.join(', ') : '(none selected)'}`,
      `Budget: ${budget || '(not specified)'}`,
      `Timing: ${timing || '(not specified)'}`,
      '',
      `Business:\n${business}`,
      '',
      `Goal / what isn't working:\n${goal}`,
    ];

    try {
      const gaIds = await getGa4Ids();
      await axios.post(`${API_BASE}/enquiries/`, {
        name,
        business: business.split('\n')[0].slice(0, 120), // first line as short business label
        email,
        phone,
        message: messageLines.join('\n'),
        source: 'website_start',
        package_interest: services.length ? services.join(' · ') : null,
        market: 'uk',
        ...gaIds,
      });
      setStatus('sent');
      form.reset();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        'Something went wrong sending your brief. Please try again or email hello@brightlabs.live.'
      );
    }
  }

  return (
    <div className="min-h-screen antialiased" style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }} data-testid="v2-start">
      <V2Nav scheme="dark" />
      <section className="pt-40 pb-14 px-6 md:px-12 lg:px-20 max-w-[1200px] mx-auto">
        <V2Kicker num="—">Start a Project</V2Kicker>
        <h1 className="v2-display mt-6 max-w-[14ch] leading-[0.9]">
          Tell us the <em style={fraunces()}>outcome</em>.
        </h1>
        <p className="v2-lede mt-8 max-w-[62ch] text-[#B8B4A6]">
          Send us a short brief. We&rsquo;ll review it and come back to you within two working days — either with questions, a suggested call, or an honest &ldquo;we&rsquo;re not the right team for this&rdquo;. No pitch decks, no follow-up spam.
        </p>
      </section>

      <section className="pb-32 px-6 md:px-12 lg:px-20 max-w-[1200px] mx-auto">
        {status === 'sent' ? (
          <div className="mt-6 border-t border-white/[0.08] pt-16" data-testid="v2-start-success">
            <div className="flex items-center gap-3 text-[#E85D2F]">
              <CheckCircle2 size={22} />
              <span className="v2-mono text-[11px] uppercase tracking-[0.24em]">Brief received</span>
            </div>
            <h2 className="v2-display mt-6 max-w-[16ch] leading-[0.95]">
              Thanks. We&rsquo;ll come <em style={fraunces()}>back</em> to you shortly.
            </h2>
            <p className="v2-lede mt-8 max-w-[60ch] text-[#B8B4A6]">
              Your brief is in — Richard and Kevin have been notified. Expect a reply within two working days, either with follow-up questions, a suggested call, or an honest &ldquo;we&rsquo;re not the right team for this&rdquo;.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/" className="v2-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#07070C] font-bold transition hover:bg-[#F5F1E8] active:scale-[0.98]" data-testid="v2-start-back-home">
                Back to home <ArrowRight size={13} />
              </Link>
              <Link to="/work" className="v2-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 px-6 py-3.5 rounded-full ring-1 ring-white/25 hover:ring-white/60 text-white transition" data-testid="v2-start-view-work">
                See our work <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        ) : (
          <form
            className="mt-6 grid gap-10 border-t border-white/[0.08] pt-14"
            data-testid="v2-start-form"
            onSubmit={handleSubmit}
          >
            <Field n="01" label="What can we help with?" hint="Pick as many as apply">
              <div className="flex flex-wrap gap-2">
                {['Website / Digital','Branding / Creative','Social Media','Paid Growth','SEO','CRM / Automation','Software / Application','Multiple services','Not sure yet'].map((o) => (
                  <label key={o} className="v2-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full ring-1 ring-white/15 hover:ring-white/40 cursor-pointer text-[#B8B4A6] hover:text-white transition-colors">
                    <input type="checkbox" name="services" value={o} className="hidden peer" />
                    <span className="peer-checked:text-[#E85D2F]">{o}</span>
                  </label>
                ))}
              </div>
            </Field>

            <Field n="02" label="Tell us about your business.">
              <textarea data-testid="v2-start-business" name="business" rows={3} placeholder="Name, sector, where you're based, what you do." required className="w-full bg-transparent border-b border-white/15 focus:border-white/50 outline-none py-3 text-[17px] text-white placeholder-[#9C978A] transition-colors" />
            </Field>

            <Field n="03" label="What are you trying to achieve? What isn't working?">
              <textarea data-testid="v2-start-goal" name="goal" rows={4} placeholder="Be as honest as you like — the more we know, the better we can respond." required className="w-full bg-transparent border-b border-white/15 focus:border-white/50 outline-none py-3 text-[17px] text-white placeholder-[#9C978A] transition-colors" />
            </Field>

            <Field n="04" label="Approximate budget?">
              <div className="flex flex-wrap gap-2">
                {['Under £1,000','£1,000-£2,500','£2,500-£5,000','£5,000-£10,000','£10,000+','Not sure yet'].map((b) => (
                  <label key={b} className="v2-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full ring-1 ring-white/15 hover:ring-white/40 cursor-pointer text-[#B8B4A6] hover:text-white transition-colors">
                    <input type="radio" name="budget" value={b} className="hidden peer" />
                    <span className="peer-checked:text-[#E85D2F]">{b}</span>
                  </label>
                ))}
              </div>
            </Field>

            <Field n="05" label="When are you looking to start?">
              <div className="flex flex-wrap gap-2">
                {['This month','Next month','This quarter','Just exploring'].map((b) => (
                  <label key={b} className="v2-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full ring-1 ring-white/15 hover:ring-white/40 cursor-pointer text-[#B8B4A6] hover:text-white transition-colors">
                    <input type="radio" name="timing" value={b} className="hidden peer" />
                    <span className="peer-checked:text-[#E85D2F]">{b}</span>
                  </label>
                ))}
              </div>
            </Field>

            <Field n="06" label="How do we reach you?">
              <div className="grid md:grid-cols-3 gap-6">
                <input data-testid="v2-start-name" name="name" required placeholder="Your name" className="bg-transparent border-b border-white/15 focus:border-white/50 outline-none py-3 text-white placeholder-[#9C978A]" />
                <input data-testid="v2-start-email" name="email" required type="email" placeholder="Email" className="bg-transparent border-b border-white/15 focus:border-white/50 outline-none py-3 text-white placeholder-[#9C978A]" />
                <input data-testid="v2-start-phone" name="phone" placeholder="Phone (optional)" className="bg-transparent border-b border-white/15 focus:border-white/50 outline-none py-3 text-white placeholder-[#9C978A]" />
              </div>
            </Field>

            {status === 'error' && (
              <div
                role="alert"
                className="text-[13px] text-[#FFB4A2] bg-[#F43F5E]/10 border border-[#F43F5E]/30 rounded-xl px-4 py-3"
                data-testid="v2-start-error"
              >
                {errorMsg}
              </div>
            )}

            <div className="pt-6 border-t border-white/[0.08] flex flex-wrap items-center justify-between gap-4">
              <p className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] max-w-[52ch]">
                We keep our client roster deliberately controlled. We&rsquo;ll only come back if we believe we&rsquo;re the right team for the work.
              </p>
              <button
                type="submit"
                data-testid="v2-start-submit"
                disabled={status === 'sending'}
                className="v2-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#E85D2F] hover:bg-[#F26F3E] text-[#07070C] font-bold transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? 'Sending…' : (<>Send brief <ArrowRight size={13} /></>)}
              </button>
            </div>
          </form>
        )}
      </section>
      <V2Footer />
    </div>
  );
}

function Field({ n, label, hint, children }) {
  return (
    <div className="grid md:grid-cols-[80px_1fr] gap-6 md:gap-12">
      <div className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A] pt-3">{n}</div>
      <div>
        <div className="v2-h3 text-white mb-1">{label}</div>
        {hint && <div className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] mb-4">{hint}</div>}
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
